import { Component, OnInit } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { timer, Subject, Observable } from 'rxjs';
import { switchMap, tap, takeUntil } from 'rxjs/operators';

import { BaseComponent } from '../../base/base.component';
import { WizardService } from '../../../services/wizard/wizard.service';
import { EventService } from '../../../services/event/event.service';
import { ErrorService } from '../../../services/error/error.service';
import { SemanticSearchService } from '../../../services/semantic-search/semantic-search.service';
import { WizardContactService } from '../../../services/wizard-contact/wizard-contact.service';
import { IContactSchema } from 'common-lib';

import { IWizardContact, INewWizardContact } from '../../../models';

declare var $: any;
import * as _ from 'lodash';

interface ITeamContact {
    email: string[];
    id: string;
    members: IContactSchema[];
    name: string;
    team: boolean;
}

@Component({
    selector: 'wizard-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss', '../../form-styles.scss'],
    providers: [TitleCasePipe]
})
export class SearchComponent extends BaseComponent implements OnInit {
    public query = '';
    public resultsCategoryLabel = 'Results';
    public hasTyped = false;
    public selection$: Observable<IWizardContact[]>;
    public isPlugin = false;
    public isAddingContact = false;
    public showLoadingIndicator$ = new Subject<boolean>();
    public showLoadingIndicator = false;
    private destroy$ = new Subject();

    /** we need to inject semanticSearchService for component to work */
    constructor(
        public wizardService: WizardService,
        public semanticSearchService: SemanticSearchService,
        public eventService: EventService,
        private errorService: ErrorService,
        private wizardContactService: WizardContactService,
        private titleCasePipe: TitleCasePipe
    ) {
        super();
    }

    ngOnInit() {
        this.isPlugin = this.wizardService.config.isPlugin;
        this.initSearchResultsTemplate();
        this.initLoadingIndicator();
        this.initSearch();

        this.subscribeToSelections();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    initSearch() {
        let searchSettings = {
            apiSettings: {
                action: 'get contacts',
                beforeSend: (settings) => {
                    this.showLoadingIndicator$.next(true);

                    if (!this.query) {
                        this.resultsCategoryLabel = 'Recent';
                        settings.action = 'get recent contacts';
                    } else {
                        this.resultsCategoryLabel = 'Results';
                    }
                    return settings;
                },
                onResponse: (data) => {
                    this.showLoadingIndicator$.next(false);

                    if (_.isEmpty(data)) {
                        return;
                    }

                    return this.restructureResponse(data);
                },
                onError: () => {
                    this.showLoadingIndicator$.next(false);
                    $('body').toast({
                        class: 'error',
                        message: `Sorry, something went wrong while searching your contacts. Please try again.`
                    });
                }
            },
            type: 'category',
            fields: {
                description: 'email',
                title: 'name',
                added: 'added'
            },
            onSelect: (result) => {
                if (result) {
                    this.handleSelect(result.payload);
                } else {
                    this.handleAddNewContact();
                }
            },
            transition: 'slide down',
            duration: 300,
            searchDelay: 300,
            minCharacters: 0
        };

        $('#search').search(searchSettings);
    }

    initSearchResultsTemplate() {
        $.fn.search.settings.templates = {
            ...$.fn.search.settings.templates,
            category: function (response, fields) {
                var html = '';
                if (!!response[fields.categoryResults]) {
                    _.forEach(response[fields.categoryResults], (category) => {
                        if (!!category[fields.results] && category.results.length > 0) {
                            html += '<div class="category category-container">';
                            if (category.name === 'Recent' || category.name === 'Results') {
                                html +=
                                    '<div class="result add-contact"><div class="icon"></div><a class="add-contact-button">+ Add New Contact</a></div>';
                            }
                            html += `<div class="name category-header">${
                                category[fields.categoryName] || 'Results'
                            }</div>`;
                            html += '<div class="results">';
                            _.forEach(category.results, (result) => {
                                html += '<a class="result"><div class="content"><div class="icon">';
                                if (result[fields.added]) {
                                    html += '<i class="ui icon check circle teal"></i>';
                                }
                                html += '</div>';
                                html += `<span class="title">${result[fields.title] || ''}</span>
                                        <span class="description">
                                        ${result[fields.description] || 'email not found'}
                                        </span>`;
                                html += '' + '</div></a>';
                            });
                            html += '</div></div>';
                        }
                    });
                    return html;
                }
                return false;
            },
            message: (message: string, type: string | 'empty', header: string) => {
                var html = '';
                if (message !== undefined && type !== undefined) {
                    html += '' + '<div class="message ' + type + '">';
                    if (header && this.query) {
                        html +=
                            '' + '<div class="header">' + "Sorry, we couldn't find who you're looking for." + '</div>';
                    }
                    html +=
                        '<div class="result add-contact no-results"><div class="icon"></div><a class="add-contact-button">+ Add New Contact</a></div>';
                    html += '</div>';
                }
                // NOTE: We add the 'result' class to the above to trick Semantic into passing it into the onSelect() callback
                return html;
            }
        };
    }

    private initLoadingIndicator() {
        this.showLoadingIndicator$
            .pipe(
                switchMap((result: boolean) => {
                    if (result === false) this.showLoadingIndicator = false;

                    return timer(1500).pipe(
                        tap((_) => {
                            this.showLoadingIndicator = result;
                        })
                    );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    restructureResponse(data) {
        const response = {
            results: {}
        };
        const maxResults = 7;

        response.results[this.resultsCategoryLabel] = {
            name: this.resultsCategoryLabel,
            results: []
        };

        _.forEach(
            _.map(data, (d) => d),
            (result, index) => {
                let resultsCategoryLabel = this.resultsCategoryLabel;

                if (index > maxResults) {
                    return false;
                }

                if (result.team) {
                    resultsCategoryLabel = 'Teams';
                    response.results[resultsCategoryLabel] = {
                        name: resultsCategoryLabel,
                        results: []
                    };

                    result.email = [result.email.join(', ')];
                }

                const isAdded = this.checkIsContactAdded(result);

                /** Re-position default email to start of array */
                if (result.defaultEmail) {
                    result.email = _.pull(result.email, result.defaultEmail);
                    result.email.unshift(result.defaultEmail);
                }

                response.results[resultsCategoryLabel].results.push({
                    name: this.titleCasePipe.transform(result.name),
                    email: result.email.join(', '),
                    added: isAdded,
                    payload: result
                });
            }
        );

        return response;
    }

    checkIsContactAdded(result) {
        const { selection } = this.wizardService;
        return _.some(selection, { id: result.id });
    }

    handleKeyup(): void {
        if (!this.query) {
            this.resultsCategoryLabel = 'Recent';
        }
    }

    handleSelect(result: ITeamContact | IContactSchema) {
        $('#search').search('set value', '').search('clear cache', '');
        this.query = '';

        if (result.team && result.members) {
            _.each(result.members, (member) => {
                this.handleSelect(member);
            });
            return;
        }

        const isAdded = this.checkIsContactAdded(result);
        if (!isAdded) {
            const shouldDeleteSubject = !this.isPlugin;
            this.wizardService.updateSelection(result as IContactSchema, { shouldDeleteSubject });
        }
    }

    handleAddNewContact() {
        this.eventService.isAddingContact = true;
    }

    public handleRemoveContact(id: string) {
        const shouldDeleteSubject = !this.isPlugin;
        this.wizardService.removeSelection(id, shouldDeleteSubject);
    }

    private subscribeToSelections() {
        this.selection$ = this.wizardService.selection$;
        this.wizardService.selection$.subscribe((selections) => {
            this.eventService.isNextDisabled = selections.length < 1;
        });
    }

    public trackSelection(_index, item) {
        return item.id;
    }

    public handleCloseAddContact() {
        this.eventService.isAddingContact = false;

        // if other contacts were added, renable the next action
        const shouldStayDisabled = this.wizardService.selection.length < 1;
        this.eventService.clearErrorMessage('add-contact', { shouldStayDisabled });
    }

    public handleAddContact(contact: INewWizardContact) {
        this.wizardContactService.addNewContact(contact).subscribe(
            (response) => {
                this.addNewContactToSelection(response);
                this.isAddingContact = this.eventService.isNextDisabled = false;
            },
            (error) => {
                this.isAddingContact = false;
                this.errorService.setServerError(error, 'add-contact');
            },
            () => {
                this.eventService.clearErrorMessage('add-contact');
            }
        );
    }

    private addNewContactToSelection(contact: IContactSchema) {
        $('body').toast({
            class: 'success',
            message: `${contact.name} has been added as a contact!`
        });
        this.isAddingContact = false;
        this.handleCloseAddContact();
        /** NOTE:
         * We do not want to reset the subject when adding new contacts in plugin mode
         * V2 of Wizard Plugin will have dynamic meeting titles but it is being 'hard-coded' for V1
         */
        const shouldDeleteSubject = !this.isPlugin;
        this.wizardService.updateSelection(contact, { shouldDeleteSubject });
    }
}
